import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
/**
 * Functional programming tools.
 *
 * @module func
 * @license Apache-2.0
 * @author drmats
 */

import { findDuplicates, head, last, range } from "./array";
import { isArray } from "./type";
export var choose = function choose(key) {
  var actions = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var defaultAction = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : function () {
    return null;
  };
  var args = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : [];
  return key in actions ? actions[key].apply(actions, _toConsumableArray(args)) : defaultAction.apply(void 0, _toConsumableArray(args));
};
export var compose = function compose() {
  for (var _len = arguments.length, fs = new Array(_len), _key = 0; _key < _len; _key++) {
    fs[_key] = arguments[_key];
  }

  return function () {
    for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      args[_key2] = arguments[_key2];
    }

    return head(fs.reduceRight(function (result, f) {
      return [f.apply(void 0, _toConsumableArray(result))];
    }, args));
  };
};
export var curry = function curry(f) {
  return curryN(f.length, f);
};
export var curryN = function curryN(n, f) {
  return function () {
    return n <= arguments.length ? f.apply(void 0, arguments) : curryN(n - arguments.length, partial(f).apply(void 0, arguments));
  };
};
export var curryThunk = function curryThunk(f) {
  return function () {
    return arguments.length === 0 ? f() : curryThunk(partial(f).apply(void 0, arguments));
  };
};
export var flow = function flow() {
  for (var _len3 = arguments.length, fs = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
    fs[_key3] = arguments[_key3];
  }

  return compose.apply(void 0, _toConsumableArray(fs.reverse()));
};
export var identity = function identity(val) {
  return val;
};
export var locker = function locker() {
  var n = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 1;
  return function (_ref) {
    var memoized = _ref.memoized,
        value = _ref.value;
    return function (thing) {
      if (memoized < n) {
        memoized += 1;
        value = thing;
      }

      return value;
    };
  }({
    memoized: 0,
    value: null
  });
};
export var partial = function partial(f) {
  return function () {
    for (var _len4 = arguments.length, init = new Array(_len4), _key4 = 0; _key4 < _len4; _key4++) {
      init[_key4] = arguments[_key4];
    }

    return function () {
      for (var _len5 = arguments.length, rest = new Array(_len5), _key5 = 0; _key5 < _len5; _key5++) {
        rest[_key5] = arguments[_key5];
      }

      return f.apply(void 0, [].concat(init, rest));
    };
  };
};
export var pipe = function pipe() {
  for (var _len6 = arguments.length, args = new Array(_len6), _key6 = 0; _key6 < _len6; _key6++) {
    args[_key6] = arguments[_key6];
  }

  return function () {
    return flow.apply(void 0, arguments).apply(void 0, args);
  };
};
export var rearg = function rearg(f) {
  return function () {
    for (var _len7 = arguments.length, indices = new Array(_len7), _key7 = 0; _key7 < _len7; _key7++) {
      indices[_key7] = arguments[_key7];
    }

    if (indices.length === 0) return f;
    if (findDuplicates(indices).length > 0) throw RangeError("func.rearg: duplicate indices are forbidden");
    var indexPairs = indices.map(function (n, o) {
      return [n, o];
    }).sort(function (_ref2, _ref3) {
      var _ref4 = _slicedToArray(_ref2, 1),
          n1 = _ref4[0];

      var _ref5 = _slicedToArray(_ref3, 1),
          n2 = _ref5[0];

      return n1 - n2;
    });
    return curryN(indices.length, function () {
      for (var _len8 = arguments.length, args = new Array(_len8), _key8 = 0; _key8 < _len8; _key8++) {
        args[_key8] = arguments[_key8];
      }

      var sargs = args.map(function (a) {
        return [a, 0];
      }),
          dargs = range(Math.max(head(last(indexPairs)) + 1, args.length)).map(function () {
        return [null, 0];
      }),
          rest = null;
      indexPairs.forEach(function (_ref6) {
        var _ref7 = _slicedToArray(_ref6, 2),
            n = _ref7[0],
            o = _ref7[1];

        dargs[n][0] = head(sargs[o]);
        dargs[n][1] += 1;
        sargs[o][1] += 1;
      });
      rest = sargs.filter(function (a) {
        return last(a) === 0;
      }).reverse();
      return f.apply(void 0, _toConsumableArray(dargs.map(function (a) {
        if (last(a) !== 0) return head(a);
        var rel = rest.pop();
        if (isArray(rel)) return head(rel);
        return rel;
      })));
    });
  };
};
export var Y = function Y(f) {
  return function (g) {
    return g(g);
  }(function (h) {
    return function () {
      return f(h(h)).apply(void 0, arguments);
    };
  });
};